import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldAlert, Landmark, Wallet, CreditCard, Send, TrendingDown, Plane, Gem } from "lucide-react";
import { cachedGet } from "@/lib/api";
import { LANDING } from "@/constants/testIds";
import { SwitchAndSave } from "@/components/SwitchAndSave";
import { GlobalSearchBar } from "@/components/GlobalSearchBar";
import { PrivateBankingCostExample } from "@/components/PrivateBankingCostExample";
import { SEO, SITE_URL } from "@/components/SEO";

const CATEGORY_ICONS = {
  monthly_account_fee: Wallet,
  domestic_atm_fee: Landmark,
  intl_atm_fee: TrendingDown,
  intl_card_fx_fee: CreditCard,
  intl_wire_fee: Send,
  fx_spread: ShieldAlert,
};

const REGION_CARDS = [
  { key: "nordics", label: "Scandinavia & Nordics", live: true },
  { key: "baltics", label: "Baltics", live: true },
  { key: "eu", label: "Rest of EU", live: true },
  { key: "eastern_europe", label: "Eastern Europe", live: true },
  { key: "switzerland", label: "Switzerland", live: true },
  { key: "uk", label: "United Kingdom", live: true },
  { key: "usa", label: "North America (USA)", live: true },
  { key: "canada", label: "North America (Canada)", live: true },
  { key: "south_america", label: "South America", live: true },
  { key: "apac", label: "Asia-Pacific", live: true },
  { key: "gcc", label: "Middle East (GCC)", live: true },
];

const Home = () => {
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    cachedGet("/stats").then((r) => setStats(r.data)).catch(() => {});
    cachedGet("/fee-categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Bankingfees.eu",
      url: SITE_URL,
      logo: `${SITE_URL}/og-image.png`,
      description: "Independent, audited comparison of retail and private banking fees worldwide.",
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Bankingfees.eu",
      url: SITE_URL,
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/banks?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
  ];

  return (
    <div>
      <SEO
        title="Compare Bank Fees Worldwide | Bankingfees.eu"
        description="Compare hidden and visible bank fees across Scandinavia, the Baltics, the EU, Switzerland, the UK, North America, South America, Asia-Pacific and the Gulf (GCC). Find the lowest-fee bank in minutes."
        keywords="compare bank fees, hidden bank fees Europe, bank fee comparison, compare bank fees Scandinavia, banking transparency"
        canonical="/"
        jsonLd={jsonLd}
      />

      {/* Global Index Status */}
      <div data-testid={LANDING.globalIndexStatusBar} className="bg-[#0F1115] text-white py-2.5 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2.5 text-xs sm:text-sm text-center">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#059669] opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#059669]" />
          </span>
          <span className="font-bold tracking-[0.15em] uppercase text-[#059669]">Global Index Status</span>
          <span className="text-white/40">·</span>
          <span className="font-semibold">
            {stats ? (
              <>
                {stats.total_banks.toLocaleString()} banks indexed live · {stats.total_private_banks.toLocaleString()} Private Banks
              </>
            ) : (
              "Loading live index…"
            )}
          </span>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-black/[0.08]">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1490351267196-b7a67e26e41b?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Global glass architecture"
            className="w-full h-full object-cover opacity-[0.14]"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <span className="inline-block text-xs tracking-[0.2em] uppercase font-bold text-[#059669] mb-6 stagger-fade-in" style={{ animationDelay: "0.05s" }}>
            Banking Transparency, Audited Bank by Bank
          </span>
          <h1 className="font-display font-black text-5xl sm:text-6xl leading-none tracking-tighter text-[#0F1115] max-w-4xl stagger-fade-in" style={{ animationDelay: "0.1s" }}>
            Bankingfees.eu is The World's #1 Search Engine for Global Banking Fees.
          </h1>
          <p
            data-testid={LANDING.heroGlobalIndexStatement}
            className="mt-5 max-w-3xl text-lg sm:text-xl font-semibold text-[#0F1115] border-l-2 border-[#059669] pl-4 leading-snug stagger-fade-in"
            style={{ animationDelay: "0.12s" }}
          >
            {stats
              ? `The official global infrastructure for pure bank-to-bank data. We only list ${stats.total_banks.toLocaleString()} licensed retail and private banks — completely filtering out asset managers, RIAs, and family offices to ensure absolute data precision across ${stats.total_private_banks.toLocaleString()} private banks.`
              : "Loading live coverage…"}
          </p>
          <div className="relative z-20 mt-10 flex flex-wrap items-center gap-4 stagger-fade-in" style={{ animationDelay: "0.25s" }}>
            <GlobalSearchBar />
            <span
              data-testid={LANDING.heroIndependentBadge}
              className="inline-flex items-center gap-2 text-xs font-bold tracking-wide uppercase text-[#059669] border border-[#059669]/30 bg-[#059669]/5 px-3 py-2 rounded-full shrink-0"
            >
              100% Independent and Free to use
            </span>
          </div>

          <div className="relative z-10 mt-8 flex flex-wrap gap-4 stagger-fade-in" style={{ animationDelay: "0.3s" }}>
            <Link
              to="/compare"
              data-testid={LANDING.heroCompareBtn}
              className="inline-flex items-center gap-2 bg-[#059669] text-white px-7 py-3.5 text-sm font-semibold hover:bg-[#0F1115] transition-colors duration-200"
            >
              Compare Banks Now <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/rankings"
              data-testid={LANDING.heroRankingsBtn}
              className="inline-flex items-center gap-2 border border-[#0F1115] text-[#0F1115] px-7 py-3.5 text-sm font-semibold hover:bg-[#0F1115] hover:text-white transition-colors duration-200"
            >
              See This Month's Rankings
            </Link>
          </div>

          {/* Stats bar */}
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 border-t border-black/[0.08] pt-10 max-w-4xl">
            <div>
              <p data-testid={LANDING.statBanksTracked} className="font-display font-black text-3xl text-[#0F1115]">{stats ? `${stats.total_banks.toLocaleString()}+` : "—"}</p>
              <p className="text-xs text-[#646973] mt-1">Banks Audited</p>
            </div>
            <div>
              <p data-testid={LANDING.statPrivateBanks} className="font-display font-black text-3xl text-[#0F1115]">{stats ? `${stats.total_private_banks.toLocaleString()}+` : "—"}</p>
              <p className="text-xs text-[#646973] mt-1">Private Banks</p>
            </div>
            <div>
              <p data-testid={LANDING.statCountries} className="font-display font-black text-3xl text-[#0F1115]">{stats ? stats.total_countries : "—"}</p>
              <p className="text-xs text-[#646973] mt-1">Countries</p>
            </div>
            <div>
              <p data-testid={LANDING.statRegions} className="font-display font-black text-3xl text-[#0F1115]">{stats ? stats.total_regions_live : "—"}</p>
              <p className="text-xs text-[#646973] mt-1">Regions Live</p>
            </div>
            <div>
              <p data-testid={LANDING.statWealthHubs} className="font-display font-black text-3xl text-[#0F1115]">{stats ? stats.total_pb_markets : "—"}</p>
              <p className="text-xs text-[#646973] mt-1">Wealth Hubs</p>
            </div>
          </div>
        </div>
      </section>

      {/* Open Data mission statement */}
      <section data-testid={LANDING.openDataSection} className="border-b border-black/[0.08] bg-white py-16 px-6 lg:px-10">
        <div className="max-w-4xl">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#059669] mb-4">Open Data. Global Scale. Zero Cost.</p>
          <p className="text-[#0F1115] leading-relaxed">
            Bankingfees.eu is built to be the universal search engine for financial transparency. Access to
            our database is entirely free for the public — no paywalls, no hidden costs.
          </p>
          <p className="mt-4 text-[#0F1115] leading-relaxed">
            By indexing {stats ? `${stats.total_banks.toLocaleString()}+` : "—"} audited banks globally — spanning deep regional coverage in the
            United States ({stats?.region_counts?.usa ?? "—"} banks) and opaque wealth management sectors
            ({stats ? stats.total_private_banks.toLocaleString() : "—"} Private Banks) — we provide unparalleled data neutrality.
          </p>
          <p className="mt-4 text-[#0F1115] leading-relaxed">
            Are you an enterprise, fintech, or financial institution looking to leverage this infrastructure?
            We offer scalable Data Licensing APIs and White-Label solutions tailored for commercial operations.
          </p>
          <Link
            to="/partners"
            data-testid={LANDING.openDataEnterpriseCta}
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#059669] hover:text-[#0F1115] transition-colors duration-200"
          >
            Explore Enterprise Solutions <ArrowUpRight size={15} />
          </Link>
        </div>
      </section>

      {/* No one else claim */}
      <section className="bg-[#0F1115] text-white py-16 px-6 lg:px-10">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#059669] mb-4">The Only Platform Of Its Kind</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl leading-tight tracking-tight">
            No one else ingests raw Open Banking transaction data to calculate ALL fees — hidden and visible — across every bank
            in Scandinavia, the Baltics, the EU, Switzerland, the UK, North America, South America, Asia-Pacific and the Gulf (GCC).
          </h2>
          <p className="mt-6 text-white/60 text-base">Bankingfees.eu does. That's why it's built for frequent global travellers and expats first.</p>
        </div>
      </section>

      {/* 6 fee categories */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#059669] mb-3">6 Standardized Fee Categories</p>
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0F1115] max-w-2xl">Audited across every single bank, in every region.</h2>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((c, i) => {
            const Icon = CATEGORY_ICONS[c.key] || Wallet;
            return (
              <div key={c.key} className="border border-black/[0.08] p-6 bg-white hover:border-[#059669] transition-colors duration-200 stagger-fade-in" style={{ animationDelay: `${i * 0.05}s` }}>
                <Icon size={22} className="text-[#059669] mb-4" />
                <h3 className="font-display font-bold text-lg text-[#0F1115]">{c.label}</h3>
                <p className="text-sm text-[#646973] mt-2 leading-relaxed">{c.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Regions grid */}
      <section className="bg-[#F5F6F8] py-20 px-6 lg:px-10 border-t border-black/[0.08]">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#059669] mb-3">Launching Simultaneously</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0F1115] max-w-2xl">The only site covering all of these regions — at once.</h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-4">
            {REGION_CARDS.map((r) => (
              <div
                key={r.key}
                data-testid={`landing-region-card-${r.key}`}
                className={`p-6 border flex items-center justify-between ${r.live ? "border-black/[0.08] bg-white" : "border-dashed border-black/[0.15] bg-transparent"}`}
              >
                <span className={`font-medium ${r.live ? "text-[#0F1115]" : "text-[#646973]"}`}>{r.label}</span>
                {r.live ? (
                  <span className="text-[10px] font-bold tracking-widest uppercase text-[#059669]">Live</span>
                ) : (
                  <span className="text-[10px] font-bold tracking-widest uppercase text-[#646973]">Coming Soon</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <SwitchAndSave />

      <PrivateBankingCostExample />

      {/* Travellers / expats */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#059669] mb-3">Built For The Frequent Traveller</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0F1115]">Especially powerful for global travellers and expats.</h2>
          <p className="mt-6 text-[#646973] leading-relaxed">
            Because Bankingfees.eu compares more banks across more regions than anyone else ever has, it's the definitive tool for
            people who live between countries and currencies.
          </p>
          <div className="mt-6 border-l-2 border-[#E52B50] pl-5">
            <p className="text-sm text-[#0F1115] leading-relaxed">
              <span className="font-semibold">Example:</span> Scandinavian banks typically charge a high fixed fee <span className="font-semibold">and</span> a bad FX rate
              every time you withdraw cash from an ATM abroad — a double hit most customers never see coming.
            </p>
          </div>
          <Link
            to="/calculator"
            data-testid={LANDING.travellerCta}
            className="mt-8 inline-flex items-center gap-2 bg-[#0F1115] text-white px-6 py-3 text-sm font-semibold hover:bg-[#059669] transition-colors duration-200"
          >
            Calculate my trip cost <Plane size={15} />
          </Link>
        </div>
        <div className="border border-black/[0.08] overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1772751320776-fb98dfba127a?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Traveller at airport lounge"
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Private Banking premium banner */}
      <section
        data-testid={LANDING.privateBankingBanner}
        className="bg-[#090A0C] border-t border-[#D4AF37]/20 py-20 px-6 lg:px-10"
      >
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-bold text-[#D4AF37] mb-5 border border-[#D4AF37]/30 px-3 py-1.5">
            <Gem size={12} /> Exclusive Sector
          </span>
          <h2 className="font-display font-black text-3xl sm:text-4xl leading-tight tracking-tight text-[#F8FAFC]">
            Private Wealth &amp; Bespoke Fee Transparency
          </h2>
          <p className="mt-5 text-[#94A3B8] max-w-2xl mx-auto leading-relaxed">
            Compare indicative fee schedules and core benchmarks for the world's premier private banking
            institutions across {stats ? stats.total_pb_markets : "—"} global wealth hub markets — UBS Private Banking, Julius Baer, Coutts,
            J.P. Morgan Private Bank, HSBC Private Banking and more.
          </p>
          <Link
            to="/private-banking"
            data-testid={LANDING.privateBankingCta}
            className="mt-9 inline-flex items-center gap-2 bg-[#D4AF37] text-[#090A0C] px-7 py-3.5 text-sm font-bold uppercase tracking-wider hover:bg-[#E6CA65] transition-colors duration-200"
          >
            Explore Private Banking <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>

      {/* B2B data licensing / white-label banner */}
      <section
        data-testid={LANDING.b2bBanner}
        className="bg-[#059669] py-5 px-6 lg:px-10"
      >
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 text-center sm:text-left">
          <div>
            <h2 className="font-display font-semibold text-base sm:text-lg tracking-tight text-white">
              Need Reliable Banking Data?
            </h2>
            <p className="mt-0.5 text-xs text-white/85 leading-snug">
              License our verified database via API or launch your own comparison service with our White Label solution.
            </p>
          </div>
          <Link
            to="/partners"
            data-testid={LANDING.b2bBannerCta}
            className="shrink-0 inline-flex items-center gap-1.5 bg-white text-[#059669] px-4 py-2 text-xs font-semibold hover:bg-[#0F1115] hover:text-white transition-colors duration-200"
          >
            Explore Our B2B Solutions <ArrowUpRight size={13} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
